import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PauseCircle, PlayCircle, CheckCircle, XCircle, Trash, EnvelopeSimple } from "@phosphor-icons/react";
import { permissionEmail } from "./permissionEmail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLE = {
  paused: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]",
  denied: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  pending: "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]",
  granted: "bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]",
};
const ACTIONS = [
  { status: "paused", label: "Pause", Icon: PauseCircle },
  { status: "pending", label: "Link-only", Icon: PlayCircle },
  { status: "granted", label: "Granted", Icon: CheckCircle },
  { status: "denied", label: "Denied", Icon: XCircle },
];

export const SourcePermissionsPanel = ({ token }) => {
  const [rows, setRows] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/source-permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data.sources))
      .catch(() => toast.error("Could not load source permissions"));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (source, status) => {
    const notes = status === "granted" ? window.prompt(`Record how permission from ${source} was obtained (email, date, contact):`, "") : "";
    if (status === "granted" && notes === null) return;
    axios.put(`${API}/admin/source-permissions/${encodeURIComponent(source)}`, { status, notes: notes || "" }, { headers })
      .then(() => { toast.success(`${source}: ${status}`); load(); })
      .catch(() => toast.error("Update failed"));
  };

  const purge = (source, count) => {
    if (!window.confirm(`Remove all ${count} ${source} stories from the site? Full copies are kept in legal hold.`)) return;
    axios.post(`${API}/admin/source-permissions/${encodeURIComponent(source)}/purge`, null, { headers })
      .then((r) => { toast.success(`${source}: ${r.data.removed} stories removed, ${r.data.preserved} preserved`); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Purge failed"));
  };

  const markRequested = (source) => {
    const sentTo = window.prompt(`Permission request for ${source} — who did you send it to? (email or contact form)`, "");
    if (sentTo === null) return;
    axios.post(`${API}/admin/source-permissions/${encodeURIComponent(source)}/requested`, { sent_to: sentTo }, { headers })
      .then(() => { toast.success(`${source}: request logged`); load(); })
      .catch(() => toast.error("Could not log request"));
  };

  const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  return (
    <div className="bg-white border border-[#E0D4C8] p-5 mb-6" data-testid="source-permissions-panel">
      <h2 className="font-serif text-lg text-[#111827] mb-1">Source permissions review</h2>
      <p className="text-xs text-[#6B6661] mb-4">
        Third-party stories are stored as headline + attribution + link only (<strong>link-only</strong>). <strong>Pause</strong>/<strong>Denied</strong> stop imports from that source; <strong>Granted</strong> records written permission and allows a first-paragraph excerpt for new imports.
      </p>
      <div className="divide-y divide-[#E5E7EB] max-h-[480px] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.source} className="py-2 flex flex-wrap items-center gap-2" data-testid={`source-row-${r.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border font-mono ${STATUS_STYLE[r.status]}`}>{r.status}</span>
            {r.full_text_allowed && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border font-mono bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">public sector / own</span>}
            <span className="text-sm text-[#33302E] flex-1 min-w-[160px]">{r.source} <span className="text-[11px] text-[#6B6661]">· {r.articles} stories</span></span>
            {r.notes && <span className="text-[11px] text-[#6B6661] w-full sm:w-auto truncate max-w-[280px]" title={r.notes}>{r.notes}</span>}
            {r.request_sent_at && (
              <span className={`px-2 py-0.5 text-[10px] font-mono border ${r.follow_up_due ? "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" : "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]"}`}
                data-testid={`${r.follow_up_due ? "follow-up-due" : "requested"}-${slug(r.source)}`} title={r.request_sent_to || ""}>
                {r.follow_up_due ? `no reply ${r.days_since_request}d — follow up` : `requested ${r.request_sent_at.slice(0, 10)}`}
              </span>
            )}
            <div className="flex gap-1 flex-wrap">
              {!r.full_text_allowed && r.status === "pending" && (
                <>
                  <a href={permissionEmail(r.source, r.articles).href} data-testid={`email-${slug(r.source)}`}
                    className="px-2 py-1 text-[11px] border border-[#BFDBFE] bg-[#EFF6FF] text-[#1E40AF] hover:bg-[#1E40AF] hover:text-white flex items-center gap-1">
                    <EnvelopeSimple size={13} /> Email request
                  </a>
                  <button onClick={() => markRequested(r.source)} data-testid={`mark-requested-${slug(r.source)}`}
                    className="px-2 py-1 text-[11px] border border-[#E0D4C8] text-[#374151] hover:border-[#1E40AF] hover:text-[#1E40AF]">
                    {r.request_sent_at ? "Re-log request" : "Mark requested"}
                  </button>
                </>
              )}
              {ACTIONS.filter((a) => a.status !== r.status).map(({ status, label, Icon }) => (
                <button key={status} onClick={() => setStatus(r.source, status)} data-testid={`set-${status}-${r.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="px-2 py-1 text-[11px] border border-[#E0D4C8] text-[#374151] hover:border-[#0F766E] hover:text-[#0F766E] flex items-center gap-1">
                  <Icon size={13} /> {label}
                </button>
              ))}
              {(r.status === "paused" || r.status === "denied") && r.articles > 0 && (
                <button onClick={() => purge(r.source, r.articles)} data-testid={`purge-${r.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="px-2 py-1 text-[11px] border border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B] hover:bg-[#DC2626] hover:text-white flex items-center gap-1">
                  <Trash size={13} /> Purge {r.articles} stories
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
